"use client";

import { cn } from "@repo/ui/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/ui/collapsible";
import type { ReasoningUIPart } from "ai";
import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";

import { MessageResponse } from "./message";
import { Shimmer } from "./shimmer";

export type ReasoningState = ReasoningUIPart["state"];

export type ReasoningProps = ComponentProps<"div"> & {
  /** The streaming reasoning text from the model. */
  text: string;
  /** Stream lifecycle — `"streaming"` while tokens are arriving, `"done"` once the part is closed. */
  state?: ReasoningState;
};

const formatDuration = (ms: number): string => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem === 0 ? `${mins}m` : `${mins}m ${rem}s`;
};

/**
 * Displays an assistant's reasoning (extended thinking) above the response.
 * Auto-expands while streaming and auto-collapses when the model finishes
 * thinking. The user can toggle open/closed at any point to override.
 *
 * Duration is only displayed for parts whose streaming → done transition this
 * component instance actually witnessed. After a page refresh the part is
 * rehydrated already in `done` state with no duration on the wire, so we fall
 * back to a label without a number ("Reasoning") rather than showing 0s.
 */
export const Reasoning = ({
  text,
  state,
  className,
  ...props
}: ReasoningProps) => {
  // Capture the part's state on first render so we can tell "rehydrated as
  // done" (no duration available) apart from "started streaming in this
  // session" (we can measure the duration ourselves).
  const initialStateRef = useRef<ReasoningState | undefined>(state);
  const wasStreamingOnMount = initialStateRef.current !== "done";

  const startedAtRef = useRef<number>(Date.now());
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const [finalDurationMs, setFinalDurationMs] = useState<number | null>(null);
  const [open, setOpen] = useState<boolean>(state !== "done");
  const userToggledRef = useRef<boolean>(false);

  const isStreaming = state !== "done";

  // Tick every second so the streaming header reflects live elapsed time.
  useEffect(() => {
    if (!isStreaming) return;
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 1000);
    return () => window.clearInterval(id);
  }, [isStreaming]);

  // Freeze final duration the moment streaming completes, then auto-collapse
  // unless the user has manually toggled. Skip when we mounted already-done —
  // `Date.now() - startedAtRef.current` would be ~0ms.
  useEffect(() => {
    if (state !== "done" || !wasStreamingOnMount) return;
    if (finalDurationMs !== null) return;
    setFinalDurationMs(Date.now() - startedAtRef.current);
    if (!userToggledRef.current) setOpen(false);
  }, [state, finalDurationMs, wasStreamingOnMount]);

  const handleOpenChange = (next: boolean) => {
    userToggledRef.current = true;
    setOpen(next);
  };

  const headerLabel =
    state === "done"
      ? finalDurationMs !== null
        ? `Thought for ${formatDuration(finalDurationMs)}`
        : "Reasoning"
      : "Thinking…";

  return (
    <Collapsible
      open={open}
      onOpenChange={handleOpenChange}
      className={cn("not-prose w-full", className)}
      {...props}
    >
      <CollapsibleTrigger
        className={cn(
          "group/reasoning flex w-full items-center gap-1.5 rounded text-left",
          "text-muted-foreground hover:text-foreground",
        )}
      >
        {isStreaming ? (
          <Shimmer as="span" className="font-medium text-xs">
            {elapsedMs >= 1000
              ? `Thinking for ${formatDuration(elapsedMs)}…`
              : "Thinking…"}
          </Shimmer>
        ) : (
          <span className="font-medium text-xs">{headerLabel}</span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden">
        <div className="mt-1 border-muted border-l-2 pl-3 text-muted-foreground text-xs">
          <MessageResponse compact>{text}</MessageResponse>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
